import {
  useTranslate,
  IResourceComponentsProps,
  useShow,
} from "@pankod/refine-core";
import { Show, Typography, BooleanField, Table, Tag } from "@pankod/refine-antd";
import dayjs from "../../dayjsConfig";

import {
  IAsset,
  IEvent,
  IEventPrize,
  IThematicData,
  IThematicEventLevel,
} from "interfaces";
import { renderLevelSummary } from "../../components/thematic/levelSummary";

const formatUtcWallClock = (val: any) =>
  val ? dayjs.utc(val).format("YYYY-MM-DD HH:mm:ss") : "-";

const { Title, Text } = Typography;

// Render a hydrated Asset as a small thumbnail with the key shown underneath
// (for debugging / copy). v2 contract: the backend ships `asset.url` ready to
// use — we don't build URLs anymore. Falls back to "-" when null.
const AssetThumb: React.FC<{ value?: IAsset | null }> = ({ value }) => {
  if (!value) return <Text>-</Text>;
  return (
    <div>
      {value.url && (
        <img
          src={value.url}
          alt=""
          style={{
            maxWidth: 200,
            maxHeight: 120,
            objectFit: "cover",
            border: "1px solid #eee",
            borderRadius: 4,
            display: "block",
          }}
        />
      )}
      {value.key && (
        <Text type="secondary" style={{ fontSize: 11, wordBreak: "break-all" }}>
          {value.key}
        </Text>
      )}
    </div>
  );
};

// Render a TranslatedString (or fall back to a plain string) consistently with
// how the existing display_name rendering works.
const TranslatedField: React.FC<{ value: any }> = ({ value }) => {
  if (!value) return <Text>-</Text>;
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, string>);
    if (entries.length === 0) return <Text>-</Text>;
    return (
      <>
        {entries.map(([lang, text]) => (
          <Text key={lang} style={{ display: "block" }}>
            <strong>{lang}:</strong> {text}
          </Text>
        ))}
      </>
    );
  }
  return <Text>{String(value)}</Text>;
};

const ThematicSection: React.FC<{ thematic: IThematicData; t: (k: string) => string }> = ({
  thematic,
  t,
}) => {
  const sortedLevels = [...(thematic.levels ?? [])].sort(
    (a, b) => a.level_number - b.level_number
  );
  return (
    <>
      <Title level={4} style={{ marginTop: 24 }}>
        {t("event.thematic.section")}
      </Title>

      <Title level={5}>{t("event.thematic.title")}</Title>
      <TranslatedField value={thematic.title} />

      <Title level={5}>{t("event.thematic.description")}</Title>
      <TranslatedField value={thematic.description} />

      <Title level={5}>{t("event.thematic.character_key")}</Title>
      <Text>{thematic.character_key || "-"}</Text>

      <Title level={5}>{t("event.thematic.initial_lives")}</Title>
      <Text>{thematic.initial_lives}</Text>

      <Title level={5}>{t("event.thematic.card_image")}</Title>
      <AssetThumb value={thematic.card_image} />

      <Title level={5}>{t("event.thematic.banner_image")}</Title>
      <AssetThumb value={thematic.banner_image} />

      <Title level={5}>{t("event.thematic.background_image")}</Title>
      <AssetThumb value={thematic.background_image} />

      <Title level={5}>{t("event.thematic.cta_link")}</Title>
      <Text>{thematic.cta_link || "-"}</Text>

      <Title level={5}>{t("event.thematic.cta_icon_key")}</Title>
      <Text>{thematic.cta_icon_key || "-"}</Text>

      <Title level={5}>{t("event.thematic.cta_title")}</Title>
      <TranslatedField value={thematic.cta_title} />

      <Title level={5}>{t("event.thematic.cta_description")}</Title>
      <TranslatedField value={thematic.cta_description} />

      <Title level={5}>{t("event.thematic.cta_button_text")}</Title>
      <TranslatedField value={thematic.cta_button_text} />

      <Title level={5} style={{ marginTop: 16 }}>
        {t("event.thematic.levels")}
      </Title>
      {sortedLevels.length > 0 ? (
        <Table<IThematicEventLevel>
          dataSource={sortedLevels}
          rowKey={(r) =>
            r.thematic_event_level_id ?? `${r.level_number}-${r.level_type}`
          }
          pagination={false}
          size="small"
          rowClassName={(r) =>
            r.trivia_exists === false ? "thematic-level-stale" : ""
          }
        >
          <Table.Column<IThematicEventLevel>
            title={t("event.thematic.level_number")}
            dataIndex="level_number"
            width={60}
          />
          <Table.Column<IThematicEventLevel>
            title={t("event.thematic.level_type")}
            dataIndex="level_type"
            width={100}
            render={(v) => <Tag>{v}</Tag>}
          />
          <Table.Column<IThematicEventLevel>
            title={t("event.thematic.config_summary")}
            render={(_, r) => renderLevelSummary(r)}
          />
          <Table.Column<IThematicEventLevel>
            title={t("event.thematic.trivia_exists")}
            width={120}
            render={(_, r) =>
              r.trivia_exists === undefined ? (
                <Text type="secondary">-</Text>
              ) : (
                <BooleanField value={r.trivia_exists} />
              )
            }
          />
        </Table>
      ) : (
        <Text>-</Text>
      )}
      <style>{`.thematic-level-stale td { background: #fff7e6; }`}</style>
    </>
  );
};

export const EventShow: React.FC<IResourceComponentsProps> = () => {
  const t = useTranslate();

  const { queryResult } = useShow<IEvent>();
  const { data, isLoading } = queryResult;
  const record = data?.data;

  return (
    <Show isLoading={isLoading}>
      <Title level={5}>{t("event.fields.name")}</Title>
      <Text>{record?.name}</Text>

      <Title level={5}>{t("event.fields.display_name")}</Title>
      {record?.display_name && typeof record.display_name === "object" ? (
        Object.entries(record.display_name).map(([lang, text]) => (
          <Text key={lang} style={{ display: "block" }}>
            <strong>{lang}:</strong> {text as string}
          </Text>
        ))
      ) : (
        <Text>{record?.display_name}</Text>
      )}

      <Title level={5}>{t("event.fields.lifecycle_status")}</Title>
      <Text>
        {record?.lifecycle_status
          ? t(`event.lifecycle_status.${record.lifecycle_status}`)
          : "-"}
      </Text>

      <Title level={5}>{t("event.fields.type")}</Title>
      <Text>{record?.type}</Text>

      <Title level={5}>{t("event.fields.max_players")}</Title>
      <Text>{record?.max_players}</Text>

      <Title level={5}>{t("event.fields.featured")}</Title>
      <BooleanField value={record?.featured} />

      <Title level={5}>{t("event.fields.external_leaderboard")}</Title>
      <BooleanField value={record?.external_leaderboard} />

      <Title level={5}>{t("event.fields.min_app_version")}</Title>
      <Text>{record?.min_app_version || "-"}</Text>

      <Title level={5}>{t("event.fields.start_datetime")}</Title>
      <Text>{formatUtcWallClock(record?.start_datetime)}</Text>

      <Title level={5}>{t("event.fields.end_datetime")}</Title>
      <Text>{formatUtcWallClock(record?.end_datetime)}</Text>

      <Title level={5}>{t("event.fields.show_from")}</Title>
      <Text>{formatUtcWallClock(record?.show_from)}</Text>

      <Title level={5}>{t("event.fields.show_until")}</Title>
      <Text>{formatUtcWallClock(record?.show_until)}</Text>

      <Title level={5}>{t("event.fields.status")}</Title>
      <Text>{record?.status}</Text>

      <Title level={5}>{t("event.fields.player_count")}</Title>
      <Text>{record?.player_count}</Text>

      <Title level={5}>{t("event.fields.group_count")}</Title>
      <Text>{record?.group_count}</Text>

      <Title level={5}>{t("event.fields.data")}</Title>
      <Text>{record?.data}</Text>

      <Title level={5}>{t("event.fields.prizes")}</Title>
      {record?.prizes && record.prizes.length > 0 ? (
        <Table<IEventPrize>
          dataSource={[...record.prizes].sort((a, b) => a.lower_bound - b.lower_bound)}
          rowKey={(r) => `${r.lower_bound}-${r.upper_bound}`}
          pagination={false}
          size="small"
        >
          <Table.Column<IEventPrize> title={t("event.fields.lower_bound")} dataIndex="lower_bound" />
          <Table.Column<IEventPrize> title={t("event.fields.upper_bound")} dataIndex="upper_bound" />
          <Table.Column<IEventPrize> title={t("event.fields.prize")} dataIndex="prize" />
        </Table>
      ) : (
        <Text>-</Text>
      )}

      {record?.thematic && <ThematicSection thematic={record.thematic} t={t} />}
    </Show>
  );
};